/**
 * «Позвать друга»: выбор друга (и режима — для сапёра), создание общей партии
 * на сервере и переход в неё. Один и тот же блок на странице игр и внутри
 * сапёра.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SharedGame } from '../../hooks/useSharedGame';
import toast from 'react-hot-toast';
import { Swords, X, Crosshair, Skull } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/client';
import { Avatar } from '../ProfileCard';
import { SegTabs } from '../../ui/SegTabs';

export type InviteKind = 'chess' | 'checkers' | 'minesweeper';
export type MinesMode = 'hunt' | 'classic';

/** Создать партию и открыть её. rematchOf — «Ещё раз» после партии: если
 *  соперник уже нажал, сервер вернёт его партию, а не создаст вторую. */
export async function inviteToGame(kind: InviteKind, to: string, opts: { mode?: MinesMode; rematchOf?: string } = {}): Promise<string | null> {
  try {
    const r = await apiClient.raw.post('/api/social/games/invite', {
      to, kind, mode: kind === 'minesweeper' ? (opts.mode || 'hunt') : undefined, rematch_of: opts.rematchOf,
    });
    return r.data?.game_id || null;
  } catch (e: any) {
    toast.error(e?.response?.data?.detail || String(e?.message || e));
    return null;
  }
}

/** «Реванш» в итоговом окне партии с другом. Если соперник уже нажал —
 *  открываем его партию (byThem — показать «… зовёт на реванш»); иначе
 *  создаём новую с rematch_of. Стороны в реванше меняет сервер. */
export function useRematch(kind: InviteKind, game: SharedGame<unknown> | null, opponent: string) {
  const nav = useNavigate();
  const [asked, setAsked] = useState(false);
  const go = async () => {
    if (!game || asked) return;
    setAsked(true);
    const mode = game.mode === 'classic' || game.mode === 'hunt' ? game.mode : undefined;
    const gid = game.rematch || await inviteToGame(kind, opponent, { mode, rematchOf: game.id });
    if (gid) nav(`/${kind}?game=${gid}`);
    else setAsked(false);
  };
  return { byThem: !!game?.rematch && !asked, go };
}

export const InviteFriend: React.FC<{
  kind: InviteKind;
  title?: React.ReactNode;
  onClose?: () => void;
  className?: string;
}> = ({ kind, title, onClose, className }) => {
  const { t } = useTranslation('gameKit');
  const nav = useNavigate();
  const [friends, setFriends] = useState<string[] | null>(null);
  const [mode, setMode] = useState<MinesMode>('hunt');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.raw.get('/api/social/friends')
      .then(r => setFriends(r.data?.friends || []))
      .catch(() => setFriends([]));
  }, []);

  const go = async (f: string) => {
    if (busy) return;
    setBusy(true);
    const gid = await inviteToGame(kind, f, { mode });
    setBusy(false);
    if (!gid) return;
    toast.success(t('invite.sent', { friend: f }));
    onClose?.();
    nav(`/${kind}?game=${gid}`);
  };

  return (
    <div className={['sb-panel sb-reveal p-3', className].filter(Boolean).join(' ')}>
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div className="sb-h"><Swords size={14}/>{title ?? t('invite.button')}</div>
        {onClose && (
          <button type="button" onClick={onClose} className="sb-iconbtn !h-7 !min-w-7" aria-label={t('close')}><X size={14}/></button>
        )}
      </div>
      {kind === 'minesweeper' && (
        <SegTabs<MinesMode> accent value={mode} onChange={setMode} className="mb-2.5"
          items={[
            { key: 'hunt', label: <><Crosshair size={12}/>{t('minesMode.hunt')}</>, title: t('minesMode.huntHint') },
            { key: 'classic', label: <><Skull size={12}/>{t('minesMode.classic')}</>, title: t('minesMode.classicHint') },
          ]}/>
      )}
      {friends === null ? null : !friends.length ? (
        <p className="text-[13px] text-muted-foreground">{t('invite.noFriends')}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {friends.map(f => (
            <button key={f} type="button" disabled={busy} onClick={() => void go(f)}
              className="sb-press inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5 text-sm hover:border-primary/50 disabled:opacity-60">
              <Avatar username={f} has size={22}/> {f}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
